// Volta do Checkout da compra de Tentos e o checkout simulado do modo local (STRIPE_SIMULADO=1 na API).
import { useEffect, useState } from 'react';
import { PACOTES_TENTOS } from '@truco6/planos';
import { type StatusCompra, api, lerTentos } from '../comum/api.ts';
import type { Sessao } from '../comum/conta.ts';
import { medirUmaVez } from '../comum/medicao.ts';
import { ir } from '../comum/rotas.ts';
import { itemDoPacote } from './LojaTentos.tsx';
import { Tento } from './tentos-comum.tsx';
import { Tela, Topo } from './comum.tsx';

interface Situacao { status: StatusCompra; tentos: number; centavos: number; metodo: 'cartao' | 'pix' | null }

// Cartão confirma em segundos; o Pix gerado fica sendo olhado por 10 minutos, enquanto a pessoa paga.
const ESPERA_CARTAO_MS = 2_000, TENTATIVAS_CARTAO = 20, ESPERA_PIX_MS = 5_000, TENTATIVAS_PIX = 120;

export function CompraOk({ sessao, idSessao }: { sessao: Sessao; idSessao: string | null }) {
  const [s, setS] = useState<Situacao | null>(null);
  const [saldo, setSaldo] = useState<number | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!idSessao) return;
    let vivo = true, n = 0;
    async function olhar() {
      try {
        const r = await api<Situacao>(sessao, 'GET', `/tentos/compra?sessao=${encodeURIComponent(idSessao!)}`);
        if (!vivo) return;
        setS(r);
        if (r.status === 'paga') {
          setSaldo((await lerTentos(sessao)).saldo);
          const p = PACOTES_TENTOS.find((x) => x.tentos === r.tentos);
          medirUmaVez('compra-tentos:' + idSessao, 'purchase', {
            currency: 'BRL', value: r.centavos / 100, transaction_id: idSessao, payment_type: r.metodo,
            items: p ? [itemDoPacote({ ...p, desconto: 0, liberado: true })] : [],
          });
          return;
        }
        if (r.status !== 'aberta') return;
        const pix = r.metodo === 'pix';
        if (++n < (pix ? TENTATIVAS_PIX : TENTATIVAS_CARTAO)) setTimeout(() => { void olhar(); }, pix ? ESPERA_PIX_MS : ESPERA_CARTAO_MS);
      } catch (e) { if (vivo) setErro((e as Error).message); }
    }
    void olhar();
    return () => { vivo = false; };
  }, [sessao, idSessao]);

  let corpo: React.ReactNode;
  if (erro || !idSessao) corpo = <p className="erro" role="alert">{erro ?? 'Compra não encontrada.'}</p>;
  else if (!s) corpo = <p className="carregando" role="status">Confirmando o pagamento...</p>;
  else if (s.status === 'paga') {
    corpo = (
      <div className="heroi">
        <Tento tamanho={72} brilho />
        <h1>+{s.tentos} TENTOS</h1>
        <p className="sub" role="status">Compra confirmada.{saldo !== null ? ` Saldo agora: ${saldo} Tentos.` : ''}</p>
      </div>
    );
  } else if (s.status === 'aberta' && s.metodo === 'pix') {
    corpo = <p className="status" role="status">Pix gerado. Os {s.tentos} Tentos entram no saldo quando o pagamento cair.</p>;
  } else if (s.status === 'aberta') {
    corpo = <p className="carregando" role="status">Confirmando o pagamento...</p>;
  } else if (s.status === 'falhou') {
    corpo = <p className="status" role="status">O pagamento não foi concluído. Nada foi cobrado.</p>;
  } else corpo = <p className="status" role="status">Essa compra foi devolvida.</p>;

  return (
    <Tela>
      <Topo titulo="Compra de Tentos" marca />
      {corpo}
      <div className="rodape-cta">
        <button className="btn1 largo" onClick={() => ir({ tela: 'inicio' }, true)}>JOGAR</button>
        <button className="link" onClick={() => ir({ tela: 'extrato' }, true)}>Ver extrato</button>
      </div>
    </Tela>
  );
}

export function CompraSimulada({ idSessao }: { idSessao: string | null }) {
  const [pix, setPix] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  async function simular(acao: string, depois: () => void) {
    const r = await fetch('/api/dev/simular', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ acao, id: idSessao }) });
    if (r.ok) depois(); else setErro('A API simulada recusou: ' + r.status);
  }
  const ok = () => ir({ tela: 'compra-ok', sessao: idSessao }, true);
  return (
    <Tela>
      <Topo titulo="Stripe simulada" />
      <div className="cartao">
        <p>{pix ? 'Pix de teste gerado.' : 'Checkout de teste da compra de Tentos.'} Nada é cobrado.</p>
        <p className="nota">{idSessao}</p>
      </div>
      {erro && <p className="erro" role="alert">{erro}</p>}
      {!pix ? (
        <>
          <button className="btn1 largo" disabled={!idSessao} onClick={() => void simular('comprar_cartao', ok)}>PAGAR NO CARTÃO</button>
          <button className="btn2 largo" disabled={!idSessao} onClick={() => void simular('gerar_pix', () => setPix(true))}>Gerar Pix</button>
        </>
      ) : (
        <>
          <button className="btn1 largo" onClick={() => void simular('pagar_pix', ok)}>PAGAR O PIX</button>
          <button className="btn2 largo" onClick={() => void simular('vencer_pix', ok)}>Deixar o Pix vencer</button>
        </>
      )}
      <button className="link" onClick={() => history.back()}>Voltar sem pagar</button>
    </Tela>
  );
}
